import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Loader2, X, User, Plus, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import CoachForm from './admin/CoachForm';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

const CoachesAdmin = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout } = useAdminAuth();
  const [coaches, setCoaches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [activeTab, setActiveTab] = useState('en'); // Language tab
  const fileInputRef = useRef(null);
  
  // Form state with multi-language support
  const [formData, setFormData] = useState({
    name: '',
    title_en: '',
    title_tr: '',
    bio_en: '',
    bio_tr: '',
    image: '',
    specialties_en: [''],
    specialties_tr: [''],
    certifications: [''],
    languages: ['English']
  });

  useEffect(() => {
    if (isAuth) {
      fetchCoaches();
    }
  }, [isAuth]);

  const fetchCoaches = async () => {
    try {
      const response = await fetch(apiUrl("coaches/admin"));
      if (response.ok) {
        const data = await response.json();
        setCoaches(data);
      }
    } catch (error) {
      logger.error('Error fetching coaches:', error);
      toast.error('Failed to load coaches');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image size must be less than 2MB');
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setImagePreview(base64String);
        setFormData(prev => ({ ...prev, image: base64String }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, image: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeArrayItem = (field, index) => {
    if (formData[field].length > 1) {
      const newArray = formData[field].filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, [field]: newArray }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      title_en: '',
      title_tr: '',
      bio_en: '',
      bio_tr: '',
      image: '',
      specialties_en: [''],
      specialties_tr: [''],
      certifications: [''],
      languages: ['English']
    });
    setImagePreview(null);
    setActiveTab('en');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const filteredData = {
        ...formData,
        specialties_en: formData.specialties_en.filter(s => s.trim() !== ''),
        specialties_tr: formData.specialties_tr.filter(s => s.trim() !== ''),
        certifications: formData.certifications.filter(c => c.trim() !== ''),
        languages: formData.languages.filter(l => l.trim() !== '')
      };
      
      const response = await fetch(apiUrl("coaches"), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(filteredData)
      });

      if (response.ok) {
        toast.success('Coach added successfully');
        setShowAddForm(false);
        resetForm();
        fetchCoaches();
      } else {
        toast.error('Failed to add coach');
      }
    } catch (error) {
      logger.error('Error creating coach:', error);
      toast.error('Failed to add coach');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (coachId, name) => {
    if (!window.confirm(`Are you sure you want to remove "${name}" from the team?`)) {
      return;
    }

    try {
      const response = await fetch(apiUrl(`coaches/${coachId}`), {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success('Coach removed');
        fetchCoaches();
      } else {
        toast.error('Failed to remove coach');
      }
    } catch (error) {
      logger.error('Error deleting coach:', error);
      toast.error('Failed to remove coach');
    }
  };

  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Login screen
  if (!isAuth) {
    return <AdminLogin onLogin={handleLogin} title="Coaches Admin" />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AdminHeader
        title="Coaches Admin"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        onAddNew={() => setShowAddForm(true)}
        addNewLabel="Add Coach"
      />

      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Overview</h2>
          <p className="text-3xl font-bold text-blue-600">{coaches.length}</p>
          <p className="text-gray-500">Active Coaches</p>
        </div>

        {/* Coaches List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : coaches.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Coaches Yet</h3>
            <p className="text-gray-500 mb-6">Get started by adding your first coach</p>
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Coach
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {coaches.map((coach) => (
              <div
                key={coach.id}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow"
                data-testid={`coach-card-${coach.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-16 h-16 rounded-full bg-blue-100 flex-shrink-0 overflow-hidden">
                    {coach.image ? (
                      <img src={coach.image} alt={coach.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-blue-600 font-bold text-xl">
                        {coach.name.split(' ').map(n => n[0]).join('')}
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-bold text-gray-900">{coach.name}</h3>
                          {coach.title_tr && (
                            <span className="inline-flex items-center px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                              🇹🇷 TR
                            </span>
                          )}
                        </div>
                        <p className="text-blue-600 text-sm">{coach.title_en}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(coach.id, coach.name)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-coach-${coach.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    </div>
                    
                    <p className="text-gray-600 text-sm mt-2 line-clamp-2">{coach.bio_en}</p>
                    
                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {coach.specialties_en?.slice(0, 2).map((s, i) => (
                        <span key={`spec-${i}`} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                          {s}
                        </span>
                      ))}
                      {coach.certifications?.slice(0, 2).map((c, i) => (
                        <span key={`cert-${i}`} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                          {c}
                        </span>
                      ))}
                    </div>
                    
                    {/* Languages */}
                    {coach.languages && (
                      <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                        <Globe className="w-3 h-3" />
                        <span>{coach.languages.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Coach Modal */}
      {showAddForm && (
        <CoachForm
          formData={formData}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          imagePreview={imagePreview}
          isSubmitting={isSubmitting}
          fileInputRef={fileInputRef}
          onInputChange={handleInputChange}
          onArrayChange={handleArrayChange}
          onAddArrayItem={addArrayItem}
          onRemoveArrayItem={removeArrayItem}
          onImageUpload={handleImageUpload}
          onRemoveImage={removeImage}
          onSubmit={handleSubmit}
          onClose={() => { setShowAddForm(false); resetForm(); }}
        />
      )}
    </div>
  );
};

export default CoachesAdmin;
